/**
 * O cartão da escola no topo do menu do painel da Escola (`{slug}.escola.ao`): o logótipo, o
 * nome, o tipo e a província e, quando a pessoa gere mais do que uma escola, «Trocar», que abre
 * a lista das escolas (com pesquisa quando são muitas). É o gémeo do `EmpresaSeletor` do Logigate.
 *
 * Cada escola tem o seu subdomínio: trocar é uma página nova (`escola.url`), não uma visita do
 * Inertia. A sessão vale para todo o `.escola.ao` (o `SetSessionDomain`), por isso a pessoa
 * chega à outra escola já autenticada.
 *
 * A lista vem de `/minhas-escolas` (só as escolas cujo painel a pessoa pode abrir) uma vez por
 * página e fica em `window._escola_minhas_escolas`: o menu do computador e a gaveta do telemóvel
 * montam cada um o seu cartão, e os dois partilham o mesmo pedido.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronsUpDown, LayoutGrid, Loader2, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/Logigate/ui";
import req from "@/utils/req";
import { track } from "@/utils/px";

/** Uma escola da lista (`MinhasEscolasController`). */
interface EscolaDaPessoa {
    id: number;
    nome: string;
    foto_link: string | null;
    slug: string;
    /** O endereço do painel dessa escola (`https://{slug}.escola.ao`). */
    url: string;
    /** «Escola», «Universidade/Faculdade», «Centro de formação», «Creche/Infantário». */
    tipo?: string | null;
    /** A província. */
    localidade?: string | null;
}

interface EscolaSeletorProps {
    /** A escola deste painel (`props.escola.info`, o `Escola::toApp()`). */
    escolaAtual: {
        id: number;
        nome: string;
        foto_link?: string | null;
        tipo_text?: string | null;
        provincia?: string | null;
    };
}

declare global {
    interface Window {
        _escola_minhas_escolas?: EscolaDaPessoa[];
        _escola_minhas_escolas_loading?: boolean;
    }
}

/** O pedido em curso, partilhado pelos cartões que montam ao mesmo tempo. */
let pedidoEmCurso: Promise<EscolaDaPessoa[] | null> | null = null;

function carregarEscolas(): Promise<EscolaDaPessoa[] | null> {
    if (window._escola_minhas_escolas) {
        return Promise.resolve(window._escola_minhas_escolas);
    }
    if (!pedidoEmCurso) {
        window._escola_minhas_escolas_loading = true;
        pedidoEmCurso = req()
            .get("/minhas-escolas")
            .then((resposta) => {
                if (resposta?.data?.estado === "ok" && Array.isArray(resposta.data.escolas)) {
                    window._escola_minhas_escolas = resposta.data.escolas;
                    return resposta.data.escolas as EscolaDaPessoa[];
                }
                console.error("Erro ao carregar as escolas:", resposta?.data?.texto);
                return null;
            })
            .finally(() => {
                window._escola_minhas_escolas_loading = false;
                pedidoEmCurso = null;
            });
    }
    return pedidoEmCurso;
}

/** Sem acentos e em minúsculas («joao» encontra «João»). */
function semAcentos(t: string): string {
    return t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** «Escola · Luanda», sem os pedaços que faltam. */
function detalhe(tipo?: string | null, localidade?: string | null): string {
    return [tipo, localidade].filter(Boolean).join(" · ");
}

/** A partir de quantas escolas aparece a pesquisa. */
const COM_PESQUISA = 7;

/** O logótipo inteiro (os brasões não são quadrados): `object-contain` sobre fundo liso. */
const CLASSE_LOGOTIPO = "rounded-lg bg-white ring-1 ring-zinc-950/5 dark:bg-zinc-800 dark:ring-white/10 [&_img]:bg-white [&_img]:object-contain dark:[&_img]:bg-zinc-800";

export default function EscolaSeletor({ escolaAtual }: EscolaSeletorProps) {
    const [escolas, setEscolas] = useState<EscolaDaPessoa[]>(window._escola_minhas_escolas || []);
    const [aberto, setAberto] = useState(false);
    const [termo, setTermo] = useState("");
    const [aMudarPara, setAMudarPara] = useState<number | null>(null);
    const caixaPesquisa = useRef<HTMLInputElement>(null);
    const lista = useRef<HTMLUListElement>(null);

    useEffect(() => {
        let vivo = true;
        carregarEscolas().then((lidas) => {
            if (vivo && lidas) {
                setEscolas(lidas);
            }
        });
        return () => {
            vivo = false;
        };
    }, []);

    const varias = escolas.length > 1;
    const comPesquisa = escolas.length >= COM_PESQUISA;
    const daLista = escolas.find((e) => e.id === escolaAtual.id);
    const detalheActual = detalhe(escolaAtual.tipo_text ?? daLista?.tipo, escolaAtual.provincia ?? daLista?.localidade) || "Escola Inteligente";
    // A página da conta da Escola (`conta.escola.ao`) lista todas as escolas da pessoa.
    const urlTodas: string | undefined = (window as any).urls?.escola_conta;

    const visiveis = useMemo(() => {
        const t = semAcentos(termo.trim());
        if (!t) {
            return escolas;
        }
        return escolas.filter((e) => semAcentos(`${e.nome} ${e.localidade ?? ""} ${e.tipo ?? ""}`).includes(t));
    }, [escolas, termo]);

    const escolher = (escola: EscolaDaPessoa) => {
        if (escola.id === escolaAtual.id) {
            setAberto(false);
            return;
        }
        track("click", "trocar_escola", { a: "escola", md: { escola_id: escola.id } });
        setAMudarPara(escola.id);
        window.location.href = escola.url;
    };

    /** As setas andam pela lista (e da pesquisa para a primeira escola). */
    const setas = (e: React.KeyboardEvent) => {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
            return;
        }
        const botoes = Array.from(lista.current?.querySelectorAll<HTMLButtonElement>("button[data-escola]") ?? []);
        if (botoes.length === 0) {
            return;
        }
        e.preventDefault();
        const i = botoes.indexOf(document.activeElement as HTMLButtonElement);
        const proximo = e.key === "ArrowDown" ? Math.min(botoes.length - 1, i + 1) : i <= 0 ? -1 : i - 1;
        if (proximo < 0) {
            caixaPesquisa.current?.focus();
        } else {
            botoes[proximo].focus();
        }
    };

    const conteudoCartao = (
        <>
            <Avatar src={escolaAtual.foto_link} nome={escolaAtual.nome} redondo={false} className={cn("size-9", CLASSE_LOGOTIPO)} />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold leading-5 text-zinc-900 dark:text-zinc-50">{escolaAtual.nome}</span>
                <span className="block truncate text-xs leading-4 text-zinc-500 dark:text-zinc-400">{detalheActual}</span>
            </span>
        </>
    );

    const classeCartao = "flex w-full min-w-0 items-center gap-2.5 rounded-xl border border-zinc-200 bg-zinc-50/70 p-2 text-left dark:border-white/10 dark:bg-white/[0.03]";

    if (!varias) {
        return (
            <div data-escola-seletor className={classeCartao}>
                {conteudoCartao}
            </div>
        );
    }

    return (
        <Popover.Root
            open={aberto}
            onOpenChange={(v) => {
                setAberto(v);
                if (v) {
                    track("click", "escola_seletor_abrir", { a: "escola" });
                } else {
                    setTermo("");
                }
            }}
        >
            <Popover.Trigger asChild>
                <button
                    type="button"
                    data-escola-seletor
                    aria-label={`Escola: ${escolaAtual.nome}. Trocar de escola`}
                    className={cn(
                        classeCartao,
                        "group min-h-[52px] pr-2 transition-colors hover:border-zinc-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/30",
                        "data-[state=open]:border-portal/40 data-[state=open]:bg-white data-[state=open]:ring-2 data-[state=open]:ring-portal/15",
                        "dark:hover:border-white/20 dark:hover:bg-white/[0.06] dark:data-[state=open]:border-portal-claro/40 dark:data-[state=open]:bg-white/[0.06]",
                    )}
                >
                    {conteudoCartao}
                    <span className="flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-1 text-xs font-semibold text-portal-texto transition-colors group-hover:bg-portal-suave dark:text-portal-claro dark:group-hover:bg-white/5">
                        Trocar
                        <ChevronsUpDown className="size-3.5" aria-hidden />
                    </span>
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align="start"
                    side="bottom"
                    sideOffset={6}
                    collisionPadding={12}
                    aria-label="Trocar de escola"
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        if (comPesquisa) {
                            caixaPesquisa.current?.focus();
                        } else {
                            (lista.current?.querySelector<HTMLButtonElement>("button[aria-current='true']") ?? lista.current?.querySelector<HTMLButtonElement>("button[data-escola]"))?.focus();
                        }
                    }}
                    onKeyDown={setas}
                    className="z-[300] w-[min(320px,calc(100vw-24px))] overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl shadow-zinc-950/10 animate-in fade-in-0 zoom-in-95 dark:border-zinc-800 dark:bg-zinc-900"
                >
                    <div className="flex items-center justify-between px-3.5 pb-2 pt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Trocar de escola</p>
                        <span className="text-xs tabular-nums text-zinc-400">{escolas.length}</span>
                    </div>

                    {comPesquisa && (
                        <div className="px-2 pb-2">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
                                <input
                                    ref={caixaPesquisa}
                                    type="search"
                                    value={termo}
                                    onChange={(e) => setTermo(e.currentTarget.value)}
                                    placeholder="Procurar escola"
                                    aria-label="Procurar escola"
                                    className="h-10 w-full rounded-lg border border-zinc-200 bg-zinc-50 pl-8 pr-8 text-base text-zinc-800 outline-none transition-colors placeholder:text-zinc-400 focus:border-portal focus:bg-white focus:ring-2 focus:ring-portal/20 md:h-9 md:text-sm dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100 dark:focus:bg-zinc-900 [&::-webkit-search-cancel-button]:hidden"
                                />
                                {termo && (
                                    <button
                                        type="button"
                                        aria-label="Limpar"
                                        onClick={() => {
                                            setTermo("");
                                            caixaPesquisa.current?.focus();
                                        }}
                                        className="absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-200/70 hover:text-zinc-700 dark:hover:bg-white/10"
                                    >
                                        <X className="size-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <ul ref={lista} className="scroll-bonito max-h-[min(55vh,340px)] space-y-0.5 overflow-y-auto overscroll-contain px-1.5 pb-1.5">
                        {visiveis.length === 0 && (
                            <li className="px-3 py-6 text-center text-[13px] text-zinc-500">Nenhuma escola com «{termo}».</li>
                        )}
                        {visiveis.map((escola) => {
                            const actual = escola.id === escolaAtual.id;
                            const aMudar = aMudarPara === escola.id;
                            const sub = detalhe(escola.tipo, escola.localidade);
                            return (
                                <li key={escola.id}>
                                    <button
                                        type="button"
                                        data-escola
                                        aria-current={actual ? "true" : undefined}
                                        disabled={aMudarPara !== null}
                                        onClick={() => escolher(escola)}
                                        className={cn(
                                            "flex min-h-11 w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors focus-visible:outline-none disabled:cursor-wait",
                                            actual
                                                ? "bg-portal-suave dark:bg-white/5"
                                                : "hover:bg-zinc-50 focus-visible:bg-zinc-100 dark:hover:bg-white/5 dark:focus-visible:bg-white/10",
                                            aMudarPara !== null && !aMudar && "opacity-50",
                                        )}
                                    >
                                        <Avatar src={escola.foto_link} nome={escola.nome} tamanho="sm" redondo={false} className={CLASSE_LOGOTIPO} />
                                        <span className="min-w-0 flex-1">
                                            <span className={cn("block truncate text-[13px] font-medium", actual ? "text-portal-texto dark:text-white" : "text-zinc-800 dark:text-zinc-100")}>
                                                {escola.nome}
                                            </span>
                                            {sub && <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">{sub}</span>}
                                        </span>
                                        {aMudar ? (
                                            <Loader2 className="size-4 shrink-0 animate-spin text-portal dark:text-portal-claro" aria-label="A abrir" />
                                        ) : actual ? (
                                            <Check className="size-4 shrink-0 text-portal dark:text-portal-claro" aria-hidden />
                                        ) : null}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    {urlTodas && (
                        <div className="border-t border-zinc-100 p-1.5 dark:border-zinc-800">
                            <a
                                href={urlTodas}
                                className="flex min-h-10 items-center gap-2 rounded-lg px-2.5 text-[13px] font-medium text-portal-texto transition-colors hover:bg-portal-suave dark:text-portal-claro dark:hover:bg-white/5"
                            >
                                <LayoutGrid className="size-4" aria-hidden />
                                Ver todas as escolas
                            </a>
                        </div>
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
